"use strict";

var _interopRequireWildcard = require("@babel/runtime/helpers/interopRequireWildcard").default;
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = exports.CustomColorPicker = void 0;
var _lodestarUtils = require("@smartsheet/lodestar-utils");
var _react = _interopRequireWildcard(require("react"));
var _i18n = require("../../../i18n/i18n");
var _useTheme = require("../../../theme/utils/useTheme");
var _button = require("../../button");
var _colorUtilities = require("../colorPickerUtils/colorUtilities");
var _warnOnLocaleProp = require("../colorPickerUtils/warnOnLocaleProp");
var _ColorPickerColorField = require("./components/ColorPickerColorField");
var _ColorPickerInputGroup = require("./components/ColorPickerInputGroup");
var _ColorPickerSlider = require("./components/ColorPickerSlider");
var _CustomColorPickerRoot = require("./styled/CustomColorPickerRoot");
var _RightAlignedButtons = require("./styled/RightAlignedButtons");
const warnOnNoLocaleProp = (0, _warnOnLocaleProp.makeWarnOnLocaleProp)("CustomColorPicker");

// Fancy props (i.e. w/ the fancy object return)

// Union'd type that can discriminate based on the type given to the
// react component when it's used!
const CustomColorPicker = _ref => {
  let {
    className,
    clientId,
    color,
    forceReturnType,
    locale,
    onApply,
    onCancel,
    showOpacity
  } = _ref;
  if (process.env.NODE_ENV !== "production") {
    if (typeof locale !== "string") {
      warnOnNoLocaleProp();
    }
  }
  const fieldRef = (0, _react.useRef)(null);
  const [inputsValid, setInputsValid] = (0, _react.useState)(true);
  const [state, setState] = (0, _react.useState)((0, _colorUtilities.colorParser)(color));
  const {
    key
  } = (0, _useTheme.useTheme)();
  const themeMode = key;
  const keyDownHandler = (0, _react.useCallback)(e => {
    if (e.key === "Escape") {
      e.stopPropagation();
      onCancel();
    }
  }, [onCancel]);
  (0, _react.useEffect)(() => {
    document.addEventListener("keydown", keyDownHandler);
    fieldRef.current?.focus();
    return () => {
      document.removeEventListener("keydown", keyDownHandler);
    };
  }, [keyDownHandler, fieldRef]);
  const applyClickHandler = () => {
    const {
      r,
      g,
      b,
      a,
      h,
      s,
      l,
      hex
    } = state;
    switch (forceReturnType) {
      case "hex":
        if (showOpacity && a) {
          onApply(`${hex}${(0, _colorUtilities.alpha2hex)(a * 0.01)}`);
        } else {
          onApply(hex);
        }
        break;
      case "rgba":
        onApply((0, _colorUtilities.rgb2css)({
          r,
          g,
          b,
          a
        }));
        break;
      case "full":
        {
          const hexVal = (0, _colorUtilities.rgb2hex)({
            r,
            g,
            b,
            a
          });
          let alpha = a;
          if (alpha === undefined) {
            alpha = 1;
          }
          onApply({
            r,
            g,
            b,
            h,
            s,
            l,
            a: alpha,
            hex: hexVal,
            rgba: (0, _colorUtilities.rgb2css)({
              r,
              g,
              b,
              a: alpha
            }),
            hsla: (0, _colorUtilities.hsl2css)({
              h,
              s,
              l,
              a: alpha
            })
          });
        }
        break;
      default:
        if (showOpacity && a !== 1) {
          onApply((0, _colorUtilities.rgb2css)({
            r,
            g,
            b,
            a
          }));
        } else {
          onApply(hex);
        }
    }
  };
  const onHueChange = val => {
    const h = +val;
    const {
      s,
      l
    } = state;
    const newRgb = (0, _colorUtilities.hsl2rgb)({
      h,
      s,
      l
    });
    const newHex = (0, _colorUtilities.rgb2hex)(newRgb);
    setState({
      h,
      s,
      l,
      a: state.a,
      ...newRgb,
      hex: newHex
    });
  };
  const onAlphaChange = val => {
    const a = Math.round((+val * 0.01 + Number.EPSILON) * 100) / 100;
    setState({
      ...state,
      a
    });
  };
  const onFieldChange = hsl => {
    const newRgb = (0, _colorUtilities.hsl2rgb)(hsl);
    const newHex = (0, _colorUtilities.rgb2hex)(newRgb);
    setState({
      ...newRgb,
      a: state.a,
      ...hsl,
      hex: newHex
    });
  };
  const onInputsChange = hex => {
    const hsl = (0, _colorUtilities.hex2hsl)(hex);
    const rgb = (0, _colorUtilities.setRGBAtoZeroIfNull)((0, _colorUtilities.hex2rgb)(hex));
    setState({
      hex,
      ...hsl,
      ...rgb,
      a: state.a
    });
  };
  const {
    h,
    s,
    l,
    r,
    g,
    b,
    hex
  } = state;

  //since 'a' can be zero, the usual way to set the default wont work (like a = 1 in the destructuring)
  const a = state.a !== undefined ? state.a : 1;
  const alphaDisplay = Math.round(a * 100);
  return /*#__PURE__*/_react.default.createElement(_i18n.I18nContextProvider, {
    locale: locale
  }, /*#__PURE__*/_react.default.createElement(_i18n.I18NContextConsumer, null, _ref2 => {
    let {
      i18n
    } = _ref2;
    return /*#__PURE__*/_react.default.createElement(_CustomColorPickerRoot.CustomColorPickerRoot, {
      className: className,
      "data-client-id": clientId,
      themeMode: themeMode,
      role: "group",
      "aria-label": i18n._( /*i18n*/"Custom color picker")
    }, /*#__PURE__*/_react.default.createElement(_ColorPickerColorField.ColorPickerColorField, {
      ref: fieldRef,
      h: h,
      s: s,
      l: l,
      onChange: onFieldChange,
      clientId: (0, _lodestarUtils.getClientId)(clientId, "cp-ccp-color-field")
    }), /*#__PURE__*/_react.default.createElement(_ColorPickerSlider.ColorPickerSlider, {
      clientId: (0, _lodestarUtils.getClientId)(clientId, "cp-ccp-color-slider"),
      onChange: onHueChange,
      type: "color",
      color: `hsl(${+h}, 100%, 50%)`,
      maxValue: 360,
      value: h
    }), showOpacity && /*#__PURE__*/_react.default.createElement(_ColorPickerSlider.ColorPickerSlider, {
      clientId: (0, _lodestarUtils.getClientId)(clientId, "cp-ccp-alpha-slider"),
      onChange: onAlphaChange,
      type: "alpha",
      color: (0, _colorUtilities.hexNoAlpha)(hex),
      buttonColor: `rgba(${r}, ${g}, ${b}, ${alphaDisplay}%)`,
      maxValue: 100,
      value: alphaDisplay
    }), /*#__PURE__*/_react.default.createElement(_ColorPickerInputGroup.ColorPickerInputGroup, {
      color: hex,
      onChange: onInputsChange,
      onAlphaChange: onAlphaChange,
      clientId: clientId,
      locale: locale,
      opacity: alphaDisplay,
      showOpacity: showOpacity,
      validationCallback: inputsValid => {
        setInputsValid(inputsValid);
      }
    }), /*#__PURE__*/_react.default.createElement(_RightAlignedButtons.RightAlignedButtons, {
      space: "small",
      orientation: "row"
    }, /*#__PURE__*/_react.default.createElement(_button.Button, {
      clientId: (0, _lodestarUtils.getClientId)(clientId, "cp-ccp-cancel-btn"),
      appearance: "secondary",
      onClick: onCancel,
      "aria-label": i18n._( /*i18n*/"Cancel color"),
      type: "button"
    }, i18n._( /*i18n*/"Cancel")), /*#__PURE__*/_react.default.createElement(_button.Button, {
      clientId: (0, _lodestarUtils.getClientId)(clientId, "cp-ccp-apply-btn"),
      onClick: applyClickHandler,
      isDisabled: !inputsValid,
      "aria-label": i18n._( /*i18n*/"Apply color"),
      type: "button"
    }, i18n._( /*i18n*/"Apply"))));
  }));
};
exports.CustomColorPicker = CustomColorPicker;
var _default = CustomColorPicker;
exports.default = _default;
//# sourceMappingURL=CustomColorPicker.js.map